/**
 * FlowBuilder.jsx  —  drag-and-drop WhatsApp button-flow editor
 * React 18, React Flow 11, MUI v5
 */
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { nanoid } from "nanoid";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MiniMap,
  MarkerType,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  SelectionMode,
} from "reactflow";
import NodeContainer from "./NodeContainer";
import PaletteCard from "../flows/PalleteCard";
import MessageSelector from "../flows/MessageSelector";
import ReplyButton from "../flows/ReplyButtons";
import PageTitle from "../../../widgets/PageTitle";
import {
  capitalizeFirstLetter,
  getHeader,
  toastError,
} from "../../../utils/util";
import MessageBuilder from "../flows/MessageBuilder";
import { useParams } from "react-router-dom";
import useApiEp from "../../../hooks/useApiEp";
import LoadingWidget from "../../../widgets/LoadingWidget";
import MessageViewerEdit, { TypeComponentMap } from "./MessageViewerEdit";
import { LoadingButton } from "@mui/lab";
import DraftsIcon from "@mui/icons-material/Drafts";
import CustomEdge from "./CustomEdge";
import { useDialog } from "../../../context/DialogContext";
import axios from "axios";
import { toast as notify } from "react-toastify";
import { API_ENDPOINT } from "../../../utils/constants";
import BackBtn from "../../../widgets/BackBtn";
import MediaSelectionModal from "../../../widgets/MediaSelectionModal";
import { UserContext } from "../../../context/UserProvider";
/* ─────────────────────────  Node UIs  ────────────────────────── */

const MessageNode = (props) => {
  return (
    <NodeContainer {...props}>
      <Handle type="target" position="left" />
      {(props.data.category === "list" ||
        props.data.category === "chatform" ||
        props.data.category === "reply_buttons") && (
        <Handle type="source" position="right" />
      )}
      <MessageBuilder {...props} />
    </NodeContainer>
  );
};
const FlowStarter = (props) => (
  <NodeContainer {...props}>
    <Handle type="source" position="right" />
    <ReplyButton {...props} />
  </NodeContainer>
);

const nodeTypes = {
  message: MessageNode,
  starter: FlowStarter,
};
const edgeTypes = {
  custom: CustomEdge,
};
function createsCycle(source, target, edgeList) {
  // BFS from target → can we reach source?
  const g = edgeList.reduce((m, e) => {
    (m[e.source] ||= []).push(e.target);
    return m;
  }, {});
  const q = [...(g[target] || [])];
  const seen = new Set(q);
  while (q.length) {
    const n = q.shift();
    if (n === source) return true;
    (g[n] || []).forEach((nx) => {
      if (!seen.has(nx)) {
        seen.add(nx);
        q.push(nx);
      }
    });
  }
  return false;
}

const Canvas = ({ allowSelfLoop = false }) => {
  const { id } = useParams();
  const { dialog, hideMediaDialog } = useDialog();
  const { user, loading } = useContext(UserContext);
  const {
    data: chatbotData,
    isLoading,
    isError,
    error,
  } = useApiEp(`/message/get-flow-detail/${id}`);
  const [savingDraft, setSavingDraft] = useState(false);
  const [exportingFlow, setExportingFlow] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [toast, setToast] = useState("");
  const [exportTarget, setExportTarget] = useState("all");
  const { screenToFlowPosition } = useReactFlow();

  /* refs mirror state for latest graph inside callbacks */
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  nodesRef.current = nodes;
  edgesRef.current = edges;

  useEffect(() => {
    if (chatbotData) {
      setNodes(chatbotData?.nodes ?? []);
      setEdges(chatbotData?.edges ?? []);
    }
  }, [chatbotData]);
  const onNodesChange = useCallback(
    (chs) => setNodes((n) => applyNodeChanges(chs, n)),
    []
  );
  const onEdgesChange = useCallback(
    (chs) => setEdges((e) => applyEdgeChanges(chs, e)),
    []
  );

  const onConnect = useCallback(
    (params) => {
      const { source, target } = params;
      if (!allowSelfLoop && source === target) {
        return setToast("Self-loops are not allowed");
      }
      if (
        createsCycle(source, target, edgesRef.current) ||
        createsCycle(source, target, [...edgesRef.current, { source, target }])
      ) {
        return setToast("That edge would create an infinite loop");
      }
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: "custom",
            animated: true,
            style: { strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          eds
        )
      );
    },
    [allowSelfLoop]
  );

  /* stable callbacks to avoid prop-identity churn */
  const handleSelection = useCallback((selection) => {}, []);

  const handleEdgeUpdate = useCallback(
    (oldEdge, newConn) =>
      setEdges((eds) => eds.map((e) => (e.id === oldEdge.id ? newConn : e))),
    []
  );

  const getDefaultData = useCallback((type, pos) => {
    if (type === "message") {
      return {
        id: nanoid(6),
        type,
        position: pos,
        data: { type, category: Object.keys(TypeComponentMap)[0] },
      };
    } else {
      return {
        id: nanoid(6),
        type,
        position: pos,
        data: { type, text: "" },
      };
    }
  }, []);

  const addNode = useCallback(
    (type, pos) => {
      const firstNodes = nodes.filter((item) => item.type === "starter");
      if (type === "starter" && firstNodes.length > 0) {
        return setToast("Starter node is already on the screen");
      }
      setNodes((nds) => [...nds, { ...getDefaultData(type, pos) }]);
    },
    [nodes]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const type = e.dataTransfer.getData("application/reactflow");
      if (!type) return;
      addNode(type, screenToFlowPosition({ x: e.clientX, y: e.clientY }));
    },
    [addNode, screenToFlowPosition]
  );

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const activeNode = useMemo(() => {
    return nodes.find((n) => n.data.edit);
  }, [nodes]);

  const handleCloseDrawer = useCallback(() => {
    setNodes((nds) =>
      nds.map((n) => ({ ...n, data: { ...n.data, edit: false } }))
    );
  }, []);
  const getStroke = useCallback(
    (n) => (n.type === "message" ? "#1976d2" : "#9c27b0"),
    []
  );
  const saveDraft = useCallback(async () => {
    if (nodes.length < 1 || edges.length < 1) {
      notify.error("Please created a chatbot flow to save to draft!");
      return;
    }
    try {
      setSavingDraft(true);
      const response = await axios.post(
        `${API_ENDPOINT}/message/save-chatbot-flow/${id}`,
        { nodes, edges },
        getHeader()
      );
      notify.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setSavingDraft(false);
    }
  }, [nodes, edges]);
  const exportFlow = useCallback(async () => {
    try {
      setExportingFlow(true);
      const response = await axios.post(
        `${API_ENDPOINT}/message/export-chatbot-flow/${id}`,
        { exportTarget },
        getHeader()
      );
      notify.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setExportingFlow(false);
    }
  }, [exportTarget]);

  if (isLoading || loading) {
    return <LoadingWidget label="Loading..." />;
  }
  return (
    <Fragment>
      <Drawer variant="permanent" anchor="left" sx={{ width: 240 }}>
        <Box sx={{ width: 240, p: 2 }}>
          <BackBtn />
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: "bold",
              color: "primary.main",
            }}
          >
            <InfoIcon fontSize="small" />
            Flow Elements
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: "block" }}
          >
            Drag and drop elements to build your conversation flow
          </Typography>

          <PaletteCard label="Message" type="message" />
          <PaletteCard label="Starter" type="starter" />

          <LoadingButton
            sx={{ mt: 2 }}
            fullWidth
            loading={savingDraft}
            variant="contained"
            onClick={saveDraft}
            startIcon={<DraftsIcon />}
          >
            Save Draft
          </LoadingButton>
          {user?.accounts && user.accounts.length > 0 && (
            <Box
              sx={{
                mt: 4,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Export Chatbot
              </Typography>
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel id="export-account-label">
                  Select Subaccount
                </InputLabel>
                <Select
                  labelId="export-account-label"
                  id="export-account"
                  value={exportTarget}
                  label="Select Subaccount"
                  onChange={(e) => setExportTarget(e.target.value)}
                >
                  <MenuItem value="all">
                    All ({user.accounts.length}) Subaccounts
                  </MenuItem>

                  {/* Assuming subaccounts have 'id' and 'name' properties */}
                  {user.accounts.map((acc) => (
                    <MenuItem key={acc} value={acc}>
                      {acc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LoadingButton
                sx={{ mt: 2 }}
                fullWidth
                loading={exportingFlow}
                variant="outlined"
                onClick={exportFlow}
              >
                Export Flow
              </LoadingButton>
            </Box>
          )}
        </Box>
      </Drawer>

      <Box
        sx={{ height: "100vh", ml: "240px" }}
        onDrop={onDrop}
        onDragOver={onDragOver}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onSelectionChange={handleSelection}
          onEdgeUpdate={handleEdgeUpdate}
          selectionMode={SelectionMode.Partial}
          isValidConnection={() => true} // guard done in onConnect
          multiSelectionKeyCode={["Meta", "Control"]}
          selectionOnDrag
          panOnDrag={[0, 1]}
          fitView
        >
          <Background />
          <Controls />
          <MiniMap
            pannable
            zoomable
            nodeStrokeColor={getStroke}
            nodeColor={getStroke}
          />
        </ReactFlow>
      </Box>
      <Drawer open={!!activeNode} onClose={handleCloseDrawer} anchor="right">
        <Box sx={{ width: { md: 500, sm: 420 } }}>
          <IconButton size="small" onClick={handleCloseDrawer}>
            <CloseIcon fontSize="small" />
          </IconButton>
          {activeNode && (
            <Box p={2}>
              <PageTitle>
                Edit {capitalizeFirstLetter(activeNode.type)}
              </PageTitle>
              <Divider sx={{ my: 1 }} />
              <Box>
                {activeNode.type === "message" && (
                  <>
                    <Box px={3}>
                      <MessageSelector
                        value={activeNode.data.category}
                        onChange={(newV) => {
                          setNodes((nds) =>
                            nds.map((item) =>
                              item.id === activeNode.id
                                ? {
                                    ...item,
                                    data: { ...item.data, category: newV },
                                  }
                                : { ...item }
                            )
                          );
                        }}
                      />
                    </Box>
                    <MessageViewerEdit
                      id={activeNode.id}
                      data={activeNode.data}
                      handleClose={handleCloseDrawer}
                    />
                  </>
                )}
                {activeNode.type === "starter" && (
                  <Box p={2}>
                    <TextField
                      fullWidth
                      value={activeNode.data.text}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNodes((nds) =>
                          nds.map((item) =>
                            item.id === activeNode.id
                              ? {
                                  ...item,
                                  data: { ...item.data, text: value },
                                }
                              : { ...item }
                          )
                        );
                      }}
                      label="Enter the starting trigger"
                      multiline
                      maxRows={4}
                      rows={2}
                      placeholder="eg. hi, hello, Interested etc."
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Drawer>
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <MediaSelectionModal dialog={dialog} onClose={hideMediaDialog} />
    </Fragment>
  );
};

/* ───────────────────── Wrapper component ─────────────────────── */

const ChatbotFlowBuilder = () => (
  <ReactFlowProvider>
    <Canvas />
  </ReactFlowProvider>
);

export default ChatbotFlowBuilder;
